import {
  LayoutDashboard,
  ClipboardList,
  Search,
  Briefcase,
  Users,
  Wallet,
  User,
  LifeBuoy,
} from 'lucide-react';

/**
 * The reference splits the client rail into labelled groups (client.tsx: an
 * "Droomwork" group over a "Managed Team" group carrying a NEW pill). These are
 * the same items the old flat rail carried, at the same paths — only the
 * grouping and the icons are new.
 */
export const HIRING_NAV = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/',
    icon: LayoutDashboard,
    /* invitations open out of the dashboard */
    also: ['/invites'],
  },
  {
    key: 'hire-requests',
    label: 'Hire Requests',
    path: '/hire-requests',
    icon: ClipboardList,
  },
  { key: 'hire-talents', label: 'Hire Talents', path: '/hire-talents', icon: Search },
  { key: 'project', label: 'Projects', path: '/project', icon: Briefcase },
];

/* Held at '#' by the app, same as before — the /team/* routes are not linked yet. */
export const MANAGED_NAV = [
  { key: 'team', label: 'Team', path: '#', icon: Users, comingSoon: true },
];

export const ACCOUNT_NAV = [
  { key: 'wallet', label: 'Wallet', path: '/wallet', icon: Wallet },
  { key: 'profile', label: 'Profile', path: '/profile', icon: User },
  { key: 'support', label: 'Support', path: '/support', icon: LifeBuoy },
];

export const NAV_GROUPS = [
  { label: 'Droomwork', items: HIRING_NAV },
  { label: 'Managed Team', items: MANAGED_NAV, badge: 'NEW' },
  { label: 'Account', items: ACCOUNT_NAV },
];

/** Small labels for the sticky header, per the reference's path→label map. */
export const PAGE_TITLES = {
  '/': 'Dashboard',
  '/hire-requests': 'Hire Requests',
  '/profile': 'Profile',
  '/project': 'Projects',
  '/wallet': 'Wallet',
  '/support': 'Support',
  '/team/pricing': 'Team Pricing',
  '/team/invite': 'Invite Team',
  '/team/invite-success': 'Invite Sent',
  '/team/onboard': 'Team Onboarding',
  '/team/contracts/create': 'Create Contract',
  '/team/invoices/schedule': 'Schedule Invoice',
  '/team/payments': 'Payments',
  '/team/payroll': 'Payroll',
  '/team/payroll/review': 'Payroll Review',
  '/team/transactions': 'Transactions',
};

/* The reference spells its dynamic segments out the same way (client.tsx:
   `if (path.startsWith("/client/team/")) return "Team Member"`). */
const DYNAMIC_TITLES = [
  [/^\/hire-requests\/[^/]+\/[^/]+$/, 'Talent Profile'],
  [/^\/hire-requests\/[^/]+$/, 'Talent Request'],
  [/^\/project\/give-offer\//, 'Give Offer'],
  [/^\/project\/[^/]+$/, 'Project Detail'],
  [/^\/invites\/[^/]+$/, 'Invitation'],
];

export const pageTitleFor = (pathname) => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];

  const dynamic = DYNAMIC_TITLES.find(([re]) => re.test(pathname));
  if (dynamic) return dynamic[1];

  const match = Object.keys(PAGE_TITLES)
    .filter((p) => p !== '/' && pathname.startsWith(p))
    .sort((a, b) => b.length - a.length)[0];
  return match ? PAGE_TITLES[match] : 'Droomwork';
};

/**
 * Reference active matcher: exact match, or a prefix match for anything that
 * is not the home route (so detail pages keep their parent lit).
 */
export const isNavItemActive = (item, pathname) => {
  if (pathname === item.path) return true;
  if (item.path !== '#' && pathname.startsWith(`${item.path}/`)) return true;
  return (item.also ?? []).some(
    (p) => pathname === p || (p !== '/' && pathname.startsWith(`${p}/`))
  );
};

/** Header eyebrow — names the rail group the current page sits in. */
export const sectionFor = (pathname) => {
  const group = NAV_GROUPS.find((g) =>
    g.items.some((item) => isNavItemActive(item, pathname))
  );
  if (group) return group.label;
  return pathname.startsWith('/team') ? 'Managed Team' : 'Droomwork';
};
